// What the Playbooks page shows, worked out from the one read (pipeline K3; module 10 U244 to U252, U256, U257; B2018).
// Pure, so the counts can be proved to agree across the head, the tabs, the rows and the pills (the brief's checklist).
import type { BookEntry, BookObjection, BookSuggestion, Heard, PlaybookBook } from './api.js';
import { dayMonth } from '../day.js';

export type Tab = 'all' | 'waiting' | 'approved' | 'words' | 'mine' | 'allocate' | 'rules';
/** `family` is a Deals family, 'yours' (the families of the opportunities the reader holds), or null for all. */
export interface View { tab: Tab; family: string | null; company: string | null; q: string; open: string | null }

export interface Line { line: BookEntry; replies: BookEntry[]; notes: BookEntry[] }

export interface Row {
  o: BookObjection;
  /** Every usual answer the type has, every product's first, then a family's own. */
  answers: BookEntry[];
  /** The answer this view shows: the family's own when one family is picked and it has one, else every product's. */
  answer: BookEntry | null;
  lines: Line[];
  /** The words a deal reads for it: the usual answer when there is one with words, else the first reply with words under
   *  its client lines (Shane's list carries its answers as replies, pipeline K5). */
  gist: BookEntry | null;
  /** Client lines an approver retired: kept apart so one can be brought back. */
  retiredLines: BookEntry[];
  /** The product an approver set (U273): one Deals family, or none for every product. Never the deals' (K5). */
  families: string[];
  raisedOn: number;
  /** Answers and client lines not yet approved, and suggestions open on the type: what waits for an approver. */
  waiting: number;
  suggestions: BookSuggestion[];
  /** No words anyone may use or approve: no usual answer or reply written, the one there was rejected, or a gap left open. */
  needsWords: boolean;
  /** Words a deal may read: an approved usual answer, or an approved reply under an approved client line (U276). */
  inUse: boolean;
}

const LIVE: ReadonlySet<string> = new Set(['draft', 'approved', 'gap', 'open']);
/** An entry with words an approver has yet to say yes or no to. A gap or an open number has none to approve: it needs words. */
export const waits = (e: BookEntry): boolean => e.status === 'draft';

/** The list is the objections an approver listed, Shane's first (pipeline K5): what waits to be placed, is placed under one
 *  of them, or is binned is not on it. */
export function rowsOf(book: PlaybookBook, family: string | null = null): Row[] {
  const byParent = new Map<string, BookEntry[]>();
  for (const e of book.entries) if (e.parentId) byParent.set(e.parentId, [...(byParent.get(e.parentId) ?? []), e]);
  return book.objections.filter((o) => o.allocation === 'listed').map((o) => {
    const own = book.entries.filter((e) => e.objectionId === o.id);
    const answers = own.filter((e) => e.kind === 'answer' && e.status !== 'retired')
      .sort((a, b) => (a.family === null ? -1 : b.family === null ? 1 : a.family.localeCompare(b.family)));
    const live = answers.filter((a) => LIVE.has(a.status));
    const answer = (family && family !== 'yours' ? live.find((a) => a.family === family) : undefined) ?? live.find((a) => a.family === null) ?? live[0] ?? answers[0] ?? null;
    const lines = own.filter((e) => e.kind === 'objection' && e.status !== 'retired').map((line) => {
      const under = (byParent.get(line.id) ?? []).filter((e) => e.status !== 'retired').sort((a, b) => a.ordinal - b.ordinal);
      return { line, replies: under.filter((e) => e.kind === 'reply'), notes: under.filter((e) => e.kind === 'note') };
    });
    const retiredLines = own.filter((e) => e.kind === 'objection' && e.status === 'retired');
    const lineIds = new Set(lines.map((l) => l.line.id));
    // What Cobrain heard waits in To allocate, an item of its own (K8), never on a row of the list.
    const suggestions = book.suggestions.filter((s) => s.status === 'open' && s.heard === null && (s.objectionId === o.id || (s.targetEntryId !== null && (own.some((e) => e.id === s.targetEntryId) || [...byParent.entries()].some(([p, kids]) => lineIds.has(p) && kids.some((k) => k.id === s.targetEntryId))))));
    const families = o.family ? [o.family] : [];
    const waiting = answers.filter(waits).length + lines.filter((l) => waits(l.line) || l.replies.some((r) => r.status === 'draft')).length + suggestions.length;
    const worded = (e: BookEntry): boolean => e.status === 'draft' || e.status === 'approved';
    const needsWords = !answers.some(worded) && !lines.some((l) => worded(l.line) && l.replies.some(worded));
    const inUse = answers.some((a) => a.status === 'approved') || lines.some((l) => l.line.status === 'approved' && l.replies.some((r) => r.status === 'approved'));
    const gist = (answer && worded(answer) ? answer : null) ?? lines.flatMap((l) => l.replies).find(worded) ?? answer;
    return { o, answers, answer, lines, gist, retiredLines, families, raisedOn: new Set(o.raisedBy.map((r) => r.dealId)).size, waiting, suggestions, needsWords, inUse };
  });
}

/** Is the row for the product: one given that product, or one for every product. */
export const forFamily = (r: Row, f: string): boolean => r.families.length === 0 || r.families.includes(f);

/** The families of the opportunities the reader holds: an account owner's "Yours". */
export const yoursOf = (book: PlaybookBook): Set<string> => new Set(book.objections.flatMap((o) => o.raisedBy.filter((r) => r.mine).map((r) => r.family)));

const words = (s: string): string[] => s.toLowerCase().normalize('NFKD').replace(/[^\p{L}\p{N}%]+/gu, ' ').split(' ').filter((w) => w.length > 2);

/** Does the row hold the words looked for: its name, any answer, client line, reply or what a client said. */
export function matches(row: Row, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const hay = [row.o.name, ...row.answers.map((a) => a.body), ...row.lines.flatMap((l) => [l.line.body, ...l.replies.map((r) => r.body)]), ...row.o.raisedBy.map((r) => r.said ?? '')];
  return hay.some((h) => h.toLowerCase().includes(n));
}

/** The type nearest the words when nothing holds them, by the words they share; null when none shares one. */
export function closest(rows: Row[], q: string): Row | null {
  const want = new Set(words(q));
  let best: Row | null = null; let score = 0;
  for (const r of rows) {
    const have = new Set(words([r.o.name, r.answer?.body ?? '', ...r.lines.map((l) => l.line.body)].join(' ')));
    const s = [...want].filter((w) => have.has(w)).length;
    if (s > score) { score = s; best = r; }
  }
  return best;
}

/** The rows a view shows, before the tab: the family, the company and the find. Retired types are listed apart. */
export function scoped(rows: Row[], v: View, yours: Set<string>): Row[] {
  return rows.filter((r) => r.o.state !== 'retired')
    .filter((r) => v.family === null || (v.family === 'yours' ? (yours.size === 0 || [...yours].some((f) => forFamily(r, f))) : forFamily(r, v.family)))
    .filter((r) => v.company === null || r.o.raisedBy.some((x) => x.account === v.company))
    .filter((r) => matches(r, v.q));
}

export const inTab = (r: Row, tab: Tab): boolean =>
  tab === 'waiting' ? r.waiting > 0
    : tab === 'approved' ? r.inUse
      : tab === 'words' ? r.needsWords
        : tab === 'mine' ? r.suggestions.some((s) => s.mine)
          : true;

/** In the list's own order, Shane's (pipeline K5), then by name. */
export const ordered = (rows: Row[]): Row[] => [...rows].sort((a, b) => (a.o.ordinal ?? Number.MAX_SAFE_INTEGER) - (b.o.ordinal ?? Number.MAX_SAFE_INTEGER) || a.o.name.localeCompare(b.o.name));

export interface Counts {
  tabs: Record<Exclude<Tab, 'rules'>, number>; families: Record<string, number>; objections: number; opportunities: number;
  /** What waits for an approver, said apart in the head: answers and client lines, and suggestions. */
  waiting: number; suggestions: number; mine: number;
}

/** Every count the page shows, from the same rows the list draws: the tabs within the family and company picked, the pills
 *  within the tab's whole list (what pressing one would show under All), and the head over the whole store. */
export function counts(rows: Row[], v: View, yours: Set<string>, book: PlaybookBook): Counts {
  const inScope = scoped(rows, v, yours);
  const tabs = { all: inScope.length, waiting: 0, approved: 0, words: 0, mine: 0, allocate: waitingOf(book).length };
  for (const r of inScope) for (const t of ['waiting', 'approved', 'words', 'mine'] as const) if (inTab(r, t)) tabs[t]++;
  const anyFamily = scoped(rows, { ...v, family: null }, yours);
  const families: Record<string, number> = {};
  for (const f of book.families) families[f] = anyFamily.filter((r) => forFamily(r, f)).length;
  const active = rows.filter((r) => r.o.state !== 'retired');
  return {
    tabs, families,
    objections: active.length,
    opportunities: new Set(active.flatMap((r) => r.o.raisedBy.map((x) => x.dealId))).size,
    waiting: active.reduce((n, r) => n + r.waiting, 0),
    suggestions: active.reduce((n, r) => n + r.suggestions.length, 0),
    mine: book.suggestions.filter((s) => s.mine && s.status === 'open').length,
  };
}

/** One name per state, as the list's right-hand column says it. Needs words comes first: until a type has words the deals
 *  show no answer, whatever else waits on it. */
export function stateWord(r: Row, canApprove: boolean): string {
  if (r.o.state === 'retired') return 'Retired';
  if (r.needsWords) return 'Needs words';
  if (canApprove && r.waiting > 0) return `${r.waiting} to approve`;
  if (!canApprove) return r.inUse ? 'Approved' : 'Not approved yet';
  return 'Approved';
}

// ---------------------------------------------------------------------------------------------
// To allocate (pipeline K6; U274, U275): what Cobrain picked up, waiting for an approver to place it
// ---------------------------------------------------------------------------------------------

export interface Item {
  /** The item's own id: the type's, or for an objection Cobrain heard (K8) the heard words' own. */
  key: string;
  /** A type picked up (the SBC show notes'); null for an objection Cobrain heard. */
  o: BookObjection | null;
  /** An objection Cobrain heard in a message or call (K4, K8), an item of its own; null for a type. */
  s: BookSuggestion | null;
  /** What the item says: a type's name, or the client's words. */
  name: string;
  /** The usual answer it came with (the SBC show notes'), as Cobrain keeps it; none for what was only heard. */
  answer: BookEntry | null;
  /** What clients said on the deals that raised it, where a link carries their words. */
  said: BookObjection['raisedBy'];
  /** Cobrain's best match on the list, in use; null when it matched none, has not matched it yet, or the match left the list. */
  match: BookObjection | null;
  /** Has Cobrain said where it goes, a match or none: false while a type waits for the matcher. */
  matched: boolean;
  /** Why Cobrain matched it so, in its words, where it gave them. */
  why: string | null;
  raisedOn: number;
}

const inUseOnList = (book: PlaybookBook, id: string | null): BookObjection | null =>
  (id ? book.objections.find((x) => x.id === id && x.allocation === 'listed' && x.state === 'active') ?? null : null);

/** What Cobrain heard that waits for an approver (K8): a client's objection, Cobrain's own (it carries a judgement). */
const heardWaits = (s: BookSuggestion): boolean => s.status === 'open' && s.heard !== null && s.kind === 'new_objection';

/**
 * The items an approver places: the types picked up, most raised first, then by code and name; then each objection Cobrain
 * heard, the newest first (K8). A heard objection's match is the list's objection the reading took it for; one heard before
 * K8 against a type that waits takes that type's place, or its match, on the list.
 */
export function waitingOf(book: PlaybookBook): Item[] {
  const types = book.objections.filter((o) => o.allocation === 'waiting' && o.state === 'active').map((o): Item => ({
    key: o.id, o, s: null, name: o.name,
    answer: book.entries.find((e) => e.kind === 'answer' && e.objectionId === o.id && e.family === null && e.status !== 'retired') ?? null,
    said: o.raisedBy.filter((r) => r.said),
    match: inUseOnList(book, o.matchUnder),
    matched: o.matchedAt !== null,
    why: o.matchWhy,
    raisedOn: new Set(o.raisedBy.map((r) => r.dealId)).size,
  })).sort((a, b) => b.raisedOn - a.raisedOn || (a.o!.code ?? '~').localeCompare(b.o!.code ?? '~') || a.name.localeCompare(b.name));
  const heard = book.suggestions.filter(heardWaits).map((s): Item => {
    const named = s.objectionId ? book.objections.find((x) => x.id === s.objectionId) ?? null : null;
    const match = inUseOnList(book, s.objectionId) ?? inUseOnList(book, named?.placedUnder ?? null) ?? inUseOnList(book, named?.matchUnder ?? null);
    return { key: s.id, o: null, s, name: s.body, answer: null, said: [], match, matched: match !== null || named === null || named.matchedAt !== null, why: null, raisedOn: 0 };
  }).sort((a, b) => b.s!.createdAt.localeCompare(a.s!.createdAt));
  return [...types, ...heard];
}

/** Does the item hold the words looked for: its name or words, the answer it came with, what a client said, or what we answered. */
export const itemMatches = (i: Item, q: string): boolean => {
  const n = q.trim().toLowerCase();
  return !n || [i.name, i.answer?.body ?? '', ...i.said.map((x) => x.said ?? ''), i.s?.answered ?? ''].some((h) => h.toLowerCase().includes(n));
};

/** What an approver moved out of the queue, the newest first: placed, made an objection or binned, each undoable (D62);
 *  a type picked up, or an objection Cobrain heard (K8). */
export type Allocated = { key: string; at: string; o: BookObjection; s: null } | { key: string; at: string; o: null; s: BookSuggestion };
export function allocatedOf(book: PlaybookBook): Allocated[] {
  const types = book.objections.filter((o) => o.previousAllocation === 'waiting' && o.allocation !== 'waiting' && o.allocatedAt !== null)
    .map((o): Allocated => ({ key: o.id, at: o.allocatedAt!, o, s: null }));
  const heard = book.suggestions.filter((s) => s.heard !== null && s.decided !== null && s.kind === 'new_objection')
    .map((s): Allocated => ({ key: s.id, at: s.decided!.at, o: null, s }));
  return [...types, ...heard].sort((a, b) => b.at.localeCompare(a.at));
}

/** "Placed under 15% is a really high revenue share by Mike, 5 Oct", as the history says it. What Cobrain heard (K8) reads
 *  the same: placed under the list's objection its words went to, made an objection (the one its press made), or binned. */
export function allocatedWords(book: PlaybookBook, a: BookObjection | Allocated): string {
  if ('key' in a && a.s) {
    const d = a.s.decided!;
    const who = `${d.by ? ` by ${d.by}` : ''}, ${dayOf(d.at)}`;
    const under = d.under ? book.objections.find((x) => x.id === d.under) ?? null : null;
    if (a.s.status === 'rejected' || !d.under) return `Binned${who}`;
    if (under && under.allocatedAt === d.at) return `Made an objection, ${under.family ?? 'every product'}${who}`;
    return `Placed under ${under?.name ?? 'one of the list'}${who}`;
  }
  const o = 'key' in a ? a.o! : a;
  const who = `${o.allocatedBy ? ` by ${o.allocatedBy}` : ''}${o.allocatedAt ? `, ${dayOf(o.allocatedAt)}` : ''}`;
  if (o.allocation === 'placed') return `Placed under ${book.objections.find((x) => x.id === o.placedUnder)?.name ?? 'one of the list'}${who}`;
  if (o.allocation === 'listed') return `Made an objection, ${o.family ?? 'every product'}${who}`;
  return `Binned${who}`;
}

/** What a press on an item says, in the tab's toast and in Triage's strip (K6, K7): written once, so the two never differ. */
export const ALLOCATE_SAID = {
  placed: (under: string) => `Placed under ${under}: its deals read that objection's approved reply.`,
  listed: (name: string, family: string | null, approved: boolean) => `${name} is an objection of the list, ${family ?? 'every product'}${approved ? ', its answer approved' : ''}.`,
  binned: (name: string) => `${name} is binned: its deals keep the objection and show no answer.`,
  again: (name: string) => `${name} waits to be allocated again.`,
  /** What Cobrain heard (K8), in K4's words for the same acts on a heard line (U267). */
  heardPlaced: (under: string, answered: boolean) => `Approved under ${under}: the client's words${answered ? ' and our answer' : ''}. On calls from now.`,
  heardBinned: 'Set aside: not an objection.',
} as const;

/** "From the SBC Summit show notes", or where Cobrain heard it ("Heard on a call with Kirolbet, 5 Oct, minute 12:40"). */
export const itemSource = (i: Item): string => (i.s?.heard ? heardWhere(i.s.heard) : i.o?.code ? 'From the SBC Summit show notes' : 'Heard by Cobrain');

/** ". Raised on 3 opportunities: Kaizen, Betsson, Superbet", the four first then "and N more", or that none carries it yet;
 *  nothing for what Cobrain heard, whose opportunity is in where it was heard. */
export function raisedWords(i: Item): string {
  if (!i.o) return '';
  const accounts = [...new Set(i.o.raisedBy.map((x) => x.account))];
  return i.raisedOn > 0
    ? `. Raised on ${i.raisedOn} ${i.raisedOn === 1 ? 'opportunity' : 'opportunities'}: ${accounts.slice(0, 4).join(', ')}${accounts.length > 4 ? ` and ${accounts.length - 4} more` : ''}`
    : '. No opportunity carries it yet';
}

/** "Raised on 8 opportunities", or that none carries it yet: the count alone, for a card's meta line (K7); where it was
 *  heard, for what Cobrain heard (K8). */
export const raisedCount = (i: Item): string =>
  (i.s ? itemSource(i) : i.raisedOn > 0 ? `Raised on ${i.raisedOn} ${i.raisedOn === 1 ? 'opportunity' : 'opportunities'}` : 'No opportunity carries it yet');

/** The press "New objection" makes from the form: for a type, the name only when changed, the words only when changed
 *  (B2059); for what Cobrain heard (K8), always a name, and the words when there are any. With what the strip says; null
 *  when the name was left empty. */
export function newObjection(i: Item, name: string, family: string, words: string): { how: { list: { family: string | null; words?: string; name?: string } }; said: string } | null {
  const w = words.trim();
  const f = family || null;
  const n = name.replace(/\s+/g, ' ').trim();
  if (!n) return null;
  if (i.s) return { how: { list: { family: f, name: n, ...(w ? { words: w } : {}) } }, said: ALLOCATE_SAID.listed(n, f, w !== '') };
  return {
    how: { list: { family: f, ...(w && w !== (i.answer?.body ?? '') ? { words: w } : {}), ...(n !== i.name ? { name: n } : {}) } },
    said: ALLOCATE_SAID.listed(n, f, w !== ''),
  };
}

/** The name a heard objection is first given in the New objection form: the client's words, as long as a name may be. */
export const NAME_MAX = 300;
export const nameOf = (i: Item): string => (i.name.length > NAME_MAX ? `${i.name.slice(0, NAME_MAX - 1).trimEnd()}…` : i.name);

/** The list's objections in use, in Shane's order: what "Under another" offers. */
export const listedInUse = (book: PlaybookBook): BookObjection[] =>
  book.objections.filter((o) => o.allocation === 'listed' && o.state === 'active').sort((a, b) => (a.ordinal ?? 1e9) - (b.ordinal ?? 1e9));

/** The fallback rules by family, a family's own first in the families' order, then those for every product. */
export function rulesByFamily(book: PlaybookBook, family: string | null): Array<{ family: string | null; rules: BookEntry[] }> {
  const rules = book.entries.filter((e) => e.kind === 'rule' && e.status !== 'retired' && (family === null || family === 'yours' || e.family === family || e.family === null));
  const keys = [...new Set(rules.map((r) => r.family))].sort((a, b) => (a === null ? 1 : b === null ? -1 : book.families.indexOf(a) - book.families.indexOf(b)));
  return keys.map((f) => ({ family: f, rules: rules.filter((r) => r.family === f).sort((a, b) => a.ordinal - b.ordinal) }));
}

/** The view in the address, so a reload or a shared link opens the same list (U228). */
export function viewOf(search: string, canApprove: boolean): View {
  const p = new URLSearchParams(search);
  const tabs: Tab[] = canApprove ? ['all', 'waiting', 'approved', 'words', 'allocate', 'rules'] : ['all', 'approved', 'mine', 'rules'];
  const tab = (tabs as string[]).includes(p.get('tab') ?? '') ? (p.get('tab') as Tab) : 'all';
  const family = p.has('family') ? (p.get('family') === 'all' ? null : p.get('family')) : canApprove ? null : 'yours';
  return { tab, family, company: p.get('company'), q: p.get('q') ?? '', open: p.get('o') };
}

export function searchOf(v: View, canApprove: boolean): string {
  const p = new URLSearchParams();
  if (v.tab !== 'all') p.set('tab', v.tab);
  const dflt = canApprove ? null : 'yours';
  if (v.family !== dflt) p.set('family', v.family ?? 'all');
  if (v.company) p.set('company', v.company);
  if (v.q) p.set('q', v.q);
  if (v.open) p.set('o', v.open);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** "4 Oct", in the reader's own zone. */
export const dayOf = (iso: string | null): string => (iso ? dayMonth(new Date(iso)) : '');

/** Where the words came from, as a person would say it: the deck and its slide, the show notes, a suggestion. A source
 *  that reads as a file name or an id says nothing rather than print it. */
export function sourceWords(source: string | null): string | null {
  if (!source) return null;
  const deck = /^Commercial Model Objection Handling\.pptx, (slide \d+)/i.exec(source);
  if (deck) return `From Shane's deck, ${deck[1]}`;
  if (/^suggestion /i.test(source)) return 'From a suggestion an approver took';
  if (/^heard by Cobrain/i.test(source)) return 'From what Cobrain heard';
  if (/\.(pptx|xlsx|xls|docx|pdf|csv|json)\b/i.test(source) || /[0-9a-f]{8}-[0-9a-f]{4}-/i.test(source)) return null;
  // The merge wrote its own row references after the words, "(T Superbet; V5; V12)": a person reads the words alone.
  return `From ${source.replace(/\s*\([^()]*\)\s*$/, '')}`;
}

const HEARD_IN: Record<string, string> = {
  call: 'on a call', meeting: 'in a meeting', email: 'in an email', teams_chat: 'in Teams', teams_channel: 'in a Teams channel',
  whatsapp: 'on WhatsApp', cobrain_chat: "in Cobrain's chat", note: 'in a note', manual_note: 'in a note',
};

/** "Heard on a call with Kirolbet, 5 Oct, minute 12:40", as the drawing says it (compact-states.html, Heard by Cobrain); a
 *  note is about its account, and what the reader may not read of the message is left out, never marked. */
export function heardWhere(h: Heard): string {
  if (!h.kind) return 'Heard by Cobrain';
  const note = h.kind === 'note' || h.kind === 'manual_note';
  return `Heard ${HEARD_IN[h.kind] ?? 'in a message'}${h.account ? `${note ? ' about' : ' with'} ${h.account}` : ''}${h.at ? `, ${dayOf(h.at)}` : ''}${h.minute ? `, minute ${h.minute}` : ''}`;
}
